import { useMemo } from 'react';
import styled from 'styled-components';
import {
  useWidgetRect,
  useWidgetRectRef,
  WidgetRectProvider
} from '@elfsight/widget-utils';
import { clamp } from 'es-toolkit';
import type { LocationCardProps } from '../common/location-card-types';
import { BannerImageLayout } from './layout/banner-image-layout';
import { LayoutBackground } from './layout/layout-background';
import { MultiColumnLayout } from './layout/multi-column-layout';
import {
  LOCATION_CARD_MIN_COLUMN_WIDTH,
  LOCATION_CARD_OFFSET_BETWEEN_COLUMNS
} from './location-card-constants';
import {
  LocationCardPropsProvider,
  useLocationCardProps
} from './location-card-settings-context';

export const LocationCard = (settings: Partial<LocationCardProps>) => {
  return (
    <WidgetRectProvider>
      <LocationCardPropsProvider {...settings}>
        <LocationCardComponent />
      </LocationCardPropsProvider>
    </WidgetRectProvider>
  );
};

const LocationCardComponent = () => {
  const settings = useLocationCardProps();
  const rect = useWidgetRect();
  const cardRectRef = useWidgetRectRef();

  const maxColumns = useMemo(() => {
    if (rect?.width) {
      const calculated = Math.floor(
        rect?.width /
          (LOCATION_CARD_MIN_COLUMN_WIDTH +
            LOCATION_CARD_OFFSET_BETWEEN_COLUMNS) // @TODO adjust
      );
      return clamp(calculated, 1, 3);
    }

    return 1;
  }, [rect?.width]);

  return (
    <Container _textAlign={settings?.alignment} ref={cardRectRef}>
      <LayoutBackground>
        <BannerImageLayout>
          <MultiColumnLayout maxColumns={maxColumns} />
        </BannerImageLayout>
      </LayoutBackground>
    </Container>
  );
};

const Container = styled.div<{ _font?: string; _textAlign?: string }>`
  position: relative;
  width: 100%;
  height: 100%;
  ${({ _textAlign }) => !!_textAlign && `text-align: ${_textAlign}`};
  ${({ _font }) => !!_font && `font-family: ${_font}`};
`;
